import { useEffect, useMemo, useRef } from "react";

import { UpdateWaypointData } from "hooks/useUserWaypoints";
import { Icon, LeafletEventHandlerFn } from "leaflet";
import { Marker } from "react-leaflet";
import { Coords, Waypoint } from "types/app";
import { getIcon } from "utils/helpers/icons/getIcon";
import { getWaypointIconByKey } from "utils/helpers/icons/getWaypointIconName";

import { defaultIcon } from "./Icons";
import { WaypointInfoPopup } from "./WaypointInfoPopup/WaypointInfoPopup";

type WaypointMarkerProps = {
  waypoint: Waypoint;
  selected?: boolean;
  selectWaypoint: (id: Waypoint["id"] | null) => void;
  updateWaypoint: (id: Waypoint["id"], data: UpdateWaypointData) => void;
};

export const WaypointMarker = ({
  waypoint,
  selected,
  selectWaypoint,
  updateWaypoint,
}: WaypointMarkerProps) => {
  const markerRef = useRef<L.Marker | null>(null);
  const iconName = getWaypointIconByKey(waypoint.icon);
  // The same icon for the same name, a new one would re-create the marker's element (e.g. in the middle of a drag)
  const icon: Icon = useMemo(
    () => (iconName ? getIcon(iconName) : defaultIcon),
    [iconName]
  );

  const handleMove: LeafletEventHandlerFn = (e) => {
    const { lat, lng } = (e.target as L.Marker).getLatLng();
    const waypointCoords: Coords = [lat, lng];
    updateWaypoint(waypoint.id, { point: waypointCoords });
    if (selected) {
      markerRef.current?.openPopup();
    }
  };

  useEffect(() => {
    if (selected) {
      markerRef.current?.openPopup();
    } else {
      markerRef.current?.closePopup();
    }
  }, [selected]);

  return (
    <Marker
      ref={markerRef}
      draggable
      position={waypoint.point}
      icon={icon}
      eventHandlers={{
        click: () => selectWaypoint(selected ? null : waypoint.id),
        moveend: handleMove,
      }}
    >
      <WaypointInfoPopup
        heading={waypoint.name}
        description={waypoint.desc}
        links={waypoint.links}
        onPopupCloseClick={() => selectWaypoint(null)}
      />
    </Marker>
  );
};
